import Md5 from 'md5';
import dayjs from 'dayjs';
import i18n from '@/locales';
import { Params } from '@/libs/types';
import {
    getUserInfoParams,
    getUserInfo,
    getProfile,
    updateProfile,
    sendBindEmail,
    bindEmail,
    sendBindPhone,
    bindPhone,
    Password,
    updatePassword,
    getTrailCompanyInfo,
    setPassword,
} from '@/api/sys/user/profile';
import type { setPasswordData } from '@/api/sys/user/profile';
import { recordRemktSource } from '@/api/chat/remktSource';
import {
    getTranslateLang,
    localRead,
    localRemove,
    sessionRead,
    sessionRemove,
} from '@/libs/util';
import { packageV5 } from '@/mixins/usePackage';
import * as Sentry from '@sentry/vue';
import { getUserBaseInfo } from '@/api/sys/user';

export default {
    namespaced: true,
    state: {},
    mutations: {},
    actions: {
        // 获取用户相关信息
        getUserInfo({ commit, dispatch }: any, isLogin = false) {
            let params:getUserInfoParams = {};
            // 兼容扫码登录需要code_login_params参数
            if (localRead('code_login_params')) {
                params = JSON.parse(localRead('code_login_params'));
            }
            return new Promise((resolve, reject) => {
                getUserInfo(params)
                    .then((res: any) => {
                        const { data } = res;

                        // 兼容扫码登录：登录完成后清空
                        if (localRead('code_login_params')) {
                            localRemove('code_login_params');
                        }

                        // 营销参数上报
                        const remkt_source = sessionRead('REMKT_SOURCE') || '';
                        if (remkt_source) {
                            const _data = {
                                project_id: data.data.project_id,
                                sys_user_id: data.data.id,
                                remkt_source,
                                source_data: sessionRead('REMKT_SOURCE_URL') || '',
                            };
                            recordRemktSource(_data).finally(() => {
                                sessionRemove('REMKT_SOURCE');
                            });
                        }

                        // 登录页调用，并且当前语言与用户信息中的语言不一致时，直接返回数据，页面操作刷新页面
                        if (isLogin && i18n.locale && i18n.locale !== data.data.language) {
                            resolve(data);
                            return;
                        }

                        let isNewUi = data.data.is_new_ui || '0';
                        const localNewUi = localRead('ss_new_ui');
                        if (localNewUi) {
                            const [localNewUiValue, time] = localNewUi.split(',');
                            // 旧版这里直接采用本地记录，预留一些调试的入口
                            isNewUi = localNewUiValue;
                            // 20s内，采用本地的isNewUi记录
                            // if (isNewUi !== localNewUiValue && Date.now() - Number(time) <= 20000) {
                            //     isNewUi = localNewUiValue;
                            // }
                        }
                        commit('user/setIsNewUi', isNewUi, { root: true });

                        commit('user/setFee', data.data.fee, { root: true });
                        commit('user/setAvatar', data.data.avatar, { root: true });
                        commit('user/setUserName', data.data.user_name, { root: true });
                        commit('user/setNickname', data.data.nickname, { root: true });
                        commit('user/setUserId', data.data.id, { root: true });
                        commit('user/setEmail', data.data.email, { root: true });
                        commit('user/setPhone', data.data.phone, { root: true });
                        commit('user/setProjectId', data.data.project_id, { root: true });
                        commit('user/setProjectCode', data.data.project_code, { root: true });
                        commit('user/setProjectCreatedTime', data.data.project_created_time, { root: true });
                        commit('user/setRoleId', data.data.role_id, { root: true });
                        commit('user/setSuperRole', data.data.super_role, { root: true });
                        commit('user/setAccess', data.data.auth_list, { root: true });
                        commit('user/setProject', data.data.project_name, { root: true });
                        commit('user/setIsBalanceTips', data.data.is_balance_tips, { root: true });
                        commit('user/setInviteCode', data.data.invite_code, { root: true });
                        commit('user/setIsActive', data.data.is_active, { root: true });
                        commit('user/setLoginToken', data.data.login_token, { root: true });
                        commit('user/setPackage', data.data.package, { root: true });
                        commit('user/setAccountNum', data.data.account_num, { root: true });
                        commit('user/setLanguage', data.data.language, { root: true });
                        // translate_language 要求的实时性比较高，换到另外一个接口里面请求了
                        // commit('user/setTranslateLanguage', getTranslateLang(data.data.translate_language), { root: true });
                        commit('user/setIsAllBatch', data.data.is_all_batch, { root: true });
                        commit('user/setManagerId', data.data.acc_manager_id, { root: true });
                        commit('user/setIdentityId', data.data.identity_id, { root: true });
                        commit('user/setPackageType', data.data.package, { root: true });
                        commit('user/setPackageType2', data.data.package_type, { root: true });
                        commit('user/setExtraUserNum', data.data.extra_user_num, { root: true });
                        commit('user/setIsExpired', data.data.is_expired, { root: true });
                        commit('user/setAuthorizationType', data.data.authorization_type, { root: true });
                        commit('user/setIsAllUser', data.data.is_all_user, { root: true });
                        commit('user/setBalanceDay', data.data.balance_day, { root: true });

                        commit('user/setIsOwner', data.data.is_owner, { root: true });

                        // 初始化 系统模式
                        commit('user/initSystemMode', data.data.system || [], { root: true });
                        // 在线状态
                        commit('user/setOnlineStatus', data.data.online_status, { root: true });
                        commit('user/setChannelAuthType', data.data.channel_auth_type, { root: true });

                        commit('user/setOnlineTimeSwitch', data.data.online_time_switch, { root: true });
                        commit('user/setTemporaryEmail', data.data.temporary_email, { root: true });
                        commit('user/setRegisterInviteCode', data.data.register_invite_code, { root: true });
                        commit('user/setTemporaryProject', data.data.temporary_project, { root: true });
                        commit('user/setAiEmployeeNewProject', data.data.ai_employee_new_project || '0', { root: true });
                        commit('user/setUserType', data.data.user_type, { root: true });
                        commit('user/setBalance', data.data.balance, { root: true });
                        commit('user/setSource', data.data.source, { root: true });
                        commit('user/setInformation', data.data.information_hidden, { root: true });
                        commit('user/setOtherInformation', data.data.other_information_hidden, { root: true });
                        commit('user/setGoogleEmail', data.data.google_email, { root: true });
                        commit('user/setCertificationNotice', data.data.certification_notice, { root: true });
                        commit('user/setCertificationInfo', data.data.certification_info, { root: true });
                        commit('user/setNewSocket', data.data.new_socket || '0', { root: true });
                        commit('user/setTelegramId', data.data.telegram_id, { root: true });
                        commit('user/setFacebookId', data.data.facebook_id, { root: true });
                        commit('user/setWhatsappPhone', data.data.whatsapp_phone, { root: true });
                        commit('user/setAccountCreatedTime', data.data.created_time, { root: true });
                        commit('user/setIsAllMonthBalance', data.data.is_all_month_balance, { root: true });
                        commit('user/setPayManagerId', data.data.manager_id, { root: true });
                        commit('user/setCustomerSuccessId', data.data.customer_success_id || '0', { root: true });
                        commit('user/setAgentId', data.data.agent_id, { root: true });
                        commit('user/setIsInputAreaDisabledLimit', data.data.is_whatsapp_area_limit || '0', { root: true });
                        commit('user/setLimitPurchaseBm', data.data.limit_purchase_bm || '0', { root: true });

                        // 调用 ss_report 上传 注册事件（Google 授权注册）
                        const googleRegisterPendingReport = sessionRead('GOOGLE_REGISTER_PENDING_REPORT');
                        if (googleRegisterPendingReport && window.ss_report) {
                            try {
                                const reportData = JSON.parse(googleRegisterPendingReport);
                                const reportPayload = { ...reportData } as any;
                                const reportUserInfo: any = {};
                                if (data.data.email) {
                                    reportUserInfo.email = data.data.email;
                                }
                                window.ss_report('register', reportPayload, reportUserInfo);
                            } catch (e) {
                                console.error(e);
                            } finally {
                                sessionRemove('GOOGLE_REGISTER_PENDING_REPORT');
                            }
                        }

                        // 设置系统提示音
                        dispatch('user/setNoticeConfig', {
                            sound_notice: data.data.sound_notice,
                            sound_notice_interval: data.data.sound_notice_interval,
                        }, { root: true });

                        const customColumn = data.data.custom_column;
                        if (customColumn) {
                            if (customColumn.account_column) {
                                commit('user/setAccountColumn', customColumn.account_column, { root: true });
                            }
                        }

                        commit('user/setModeStatus', {
                            auth: Number(data.data.auth_token_status) !== 1,
                            token: Number(data.data.token_status) !== 1,
                        }, { root: true });

                        let packageText = i18n.t(`base.packageInfo.${data.data.package}`).toString();
                        if (packageText.includes('base.packageInfo') || data.data.package === 'basic') {
                            // 未找到对应的套餐信息
                            const packageV5Format: { [K in string]: string } = packageV5('版');
                            packageText = packageV5Format[data.data.package] || '';
                        }

                        /* 设置插件登录信息 */
                        let plugin_language = data.data.language;
                        if (i18n.locale && i18n.locale !== data.data.language) {
                            plugin_language = i18n.locale;
                        }

                        const userInfo = {
                            user_id: Md5(data.data.invite_code), // 加密后的用户 id
                            user_name: `${data.data.user_name}(${data.data.id}/${data.data.project_id})`, // 用户名
                            language: plugin_language, // 插件语言
                            phone: data.data.phone, // 手机
                            email: data.data.email, // 邮箱
                            description: `项目名称:${data.data.project_name}(${data.data.project_id})\n套餐: ${packageText}\n套餐费用: $${data.data.package_fee}\n截止日期: ${data.data.package === 'basic' ? '-' : dayjs.unix(data.data.balance_day).format('YYYY-MM-DD')}\n客户成功ID: ${data.data.customer_success_id || '-'}`, // 描述
                            custom_fields_ext: { projectid: data.data.project_id }, // 自定义字段
                        };

                        if (window.ssq) {
                            window.ssq.push('setLoginInfo', userInfo);
                            window.ssq.push('chatClose');
                        }
                        /* 设置插件登录信息 */

                        // 设置 Sentry 用户信息
                        if (Sentry && Sentry.setUser) {
                            try {
                                Sentry.setUser({
                                    id: data.data.id,
                                    project_id: data.data.project_id,
                                    language: data.data.language,
                                });
                                Sentry.setTag('user.projectId', data.data.project_id);
                            } catch (e) {
                                console.error('Sentry.setUser error:', e);
                            }
                        }

                        // 获取用户基础信息（无缓存版）
                        getUserBaseInfo()
                            .then((baseInfoRes) => {
                                commit('user/setTranslateLanguage', getTranslateLang(baseInfoRes.data.data.translate_language), { root: true });
                            });

                        resolve(data);
                    })
                    .catch((err: any) => {
                        reject(err);
                        console.log('err', err);
                    });
            });
        },
        // 获取用户信息
        getProfile({ commit }: any) {
            return new Promise((resolve, reject) => {
                getProfile()
                    .then((res: any) => {
                        const { data } = res;
                        commit('user/setUserName', data.data.user_name, { root: true });
                        commit('user/setUserId', data.data.id, { root: true });
                        commit('user/setEmail', data.data.email, { root: true });
                        commit('user/setPhone', data.data.phone, { root: true });
                        commit('user/setLanguage', data.data.language, { root: true });
                        commit('user/setTranslateLanguage', getTranslateLang(data.data.translate_language), { root: true });
                        commit('user/setTimeZone', data.data.time_zone, { root: true });
                        commit('user/setRegisterInviteCode', data.data.invite_code, { root: true });
                        commit('user/setWxOpenid', data.data.wx_openid, { root: true });
                        commit('user/setIsOpenTwoVerify', data.data.is_open_two_verify, { root: true });
                        commit('user/setPasswordEmpty', data.data.password_empty, { root: true });
                        commit('user/setGoogleEmail', data.data.google_email, { root: true });
                        commit('user/setTelegramId', data.data.telegram_id, { root: true });
                        commit('user/setFacebookId', data.data.facebook_id, { root: true });
                        commit('user/setWhatsappPhone', data.data.whatsapp_phone, { root: true });
                        resolve(res);
                    })
                    .catch((err: any) => {
                        reject(err);
                    });
            });
        },
        // 获取试用公司信息
        getTrailCompanyInfo({ commit }: any) {
            return new Promise((resolve, reject) => {
                getTrailCompanyInfo()
                    .then((res: any) => {
                        const { data } = res;
                        commit('user/setCreatedTime', data.data.list.created_time, { root: true });
                        commit('user/setUserNum', data.data.list.user_num, { root: true });
                        commit('user/setFee', data.data.list.fee, { root: true });
                        resolve(res);
                    })
                    .catch((err: any) => {
                        reject(err);
                    });
            });
        },
        // 获取用户信息
        updateProfile({ commit }: any, params: Params) {
            const data = { ...params };
            if (params.password) {
                data.password = Md5(params.password);
            }
            return new Promise((resolve, reject) => {
                updateProfile(data)
                    .then((res: any) => {
                        resolve(res);
                    })
                    .catch((err: any) => {
                        reject(err);
                    });
            });
        },
        // 修改密码
        updatePassword({ commit }: any, {
            oldPassword,
            newPassword,
        }: Password) {
            return updatePassword({
                oldPassword: Md5(oldPassword),
                newPassword: Md5(newPassword),
            });
        },

        setPassword({ commit }:any, { email, password }:setPasswordData) {
            return setPassword({
                email,
                password: Md5(password),
            });
        },

        sendBindEmail({ commit }: any, email: string) {
            return new Promise((resolve, reject) => {
                sendBindEmail(email)
                    .then((res: any) => {
                        resolve(res.data);
                    })
                    .catch((err: any) => {
                        reject(err);
                    });
            });
        },
        sendBindPhone({ commit }: any, phone: number) {
            return new Promise((resolve, reject) => {
                sendBindPhone(phone)
                    .then((res: any) => {
                        resolve(res.data);
                    })
                    .catch((err: any) => {
                        reject(err);
                    });
            });
        },
        bindEmail({ commit, dispatch }: any, { email, code }: Params) {
            return new Promise((resolve, reject) => {
                bindEmail({
                    email,
                    code,
                } as any)
                    .then((res: any) => {
                        resolve(res.data);
                    })
                    .catch((err: any) => {
                        reject(err);
                    });
            });
        },
        bindPhone({ commit, dispatch }: any, { phone, code }: Params) {
            return new Promise((resolve, reject) => {
                bindPhone({
                    phone,
                    code,
                })
                    .then((res: any) => {
                        resolve(res.data);
                    })
                    .catch((err: any) => {
                        reject(err);
                    });
            });
        },
    },
};
